import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/store/app.reducer';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';

import { Observable, Subscription, filter } from 'rxjs';
import { PermissionTemplate } from 'src/app/model/permission';
import * as PermissionActions from 'src/app/store/permissions/permissions.actions';
import { selectCurrentTemplate, selectLoadingTemplates, selectTemplateLogo, selectTemplateUpdated, selectTemplateUpdating } from 'src/app/store/permissions/permissions.selector';
import { Site } from 'src/app/model/site';
import { selectCurrentSite } from 'src/app/store/site/site.selector';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { ALLOWED_IMAGE_TYPES, EFileType, MAX_UPLOAD_SIZE } from 'src/app/conf/user-files';
import { UserFile } from 'src/app/model/user-file';
import { Notification, NotificationLevel } from 'src/app/model/notification';
import { TranslateService } from '@ngx-translate/core';
import * as NotificationsActions from 'src/app/store/notifications/notifications.actions';

@Component({
	styles: `
		#previewImage {
			max-height: 80px;
			border-radius: 10px;
		}
		
		img {
			-webkit-user-drag: none;
			user-drag: none;
		}
	`,
	templateUrl: './permission-email.dialog.component.html',
})
export class PermissionEmailDialogComponent implements OnInit, OnDestroy {
	site$: Observable<Site | undefined>;
	siteSub: Subscription | undefined;
	loadingTemplates$: Observable<boolean | undefined>;
	loadingTemplatesSub: Subscription | undefined;
	currentTemplate$: Observable<PermissionTemplate | undefined>;
	currentTemplateSub: Subscription | undefined;
	templateUpdated$: Observable<boolean | undefined>;
	templateUpdatedSub: Subscription | undefined;
	templateLogo$: Observable<string | undefined>;
	templateLogoSub: Subscription | undefined;

	updating: boolean;
	templateUpdating$: Observable<boolean | undefined>;
	templateUpdatingSub: Subscription | undefined;

	loadingTemplate: boolean;

	customEmailForm: FormGroup;
	currentSite: Site;

	currTemplateSub: Subscription;
	currentTemplate: PermissionTemplate;
	loadingTemplate$: Observable<boolean>;
	currTemplateLogoSub: Subscription;

	fileToUpload: File;
	image: string | ArrayBuffer;
	imageId = '';

	maxUploadSize = MAX_UPLOAD_SIZE;

	imgPreview: SafeUrl = '';
	canDeleteUpload: boolean;
	retryUpload = false;

	updatedSub: Subscription;
	editMode = false;

	filteredPlace: any[];
	searchControl: FormGroup;

	place: any;
	positionMap: any;
	imgWidth: number;
	imgHeight: number;

	// design
	screenWidth: number;
	MIN_MD_WIDTH = 500;
	MIN_MD_WIDTH_EXT = 670;
	MAX_MD_WIDTH = 1120;
	MIN_WIDTH_COL6_DIALOG = 1355;
	MIN_WIDTH_COL4 = 1680;
	DELTA_ERROR_PIXEL = 7;
	MIN_TWO_BUTTONS_DIALOG_WIDTH = 575 + this.DELTA_ERROR_PIXEL;

	constructor(
		private store: Store<AppState>,
		private fb: FormBuilder,
		private domSanitizer: DomSanitizer,
		private ref: DynamicDialogRef,
		private translate: TranslateService,
	) {
		this.initSelectors();
	}

	initSelectors(): void {
		// Store selectors
		this.site$ = this.store.select(selectCurrentSite);
		this.loadingTemplates$ = this.store.select(selectLoadingTemplates);
		this.currentTemplate$ = this.store.select(selectCurrentTemplate);
		this.templateUpdated$ = this.store.select(selectTemplateUpdated);
		this.templateUpdating$ = this.store.select(selectTemplateUpdating);
		this.templateLogo$ = this.store.select(selectTemplateLogo);
	}

	ngOnInit(): void {
		// eslint-disable-next-line max-len
		this.imgPreview = this.domSanitizer.bypassSecurityTrustUrl('data:image/jpeg;base64, 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');
		this.store.dispatch(PermissionActions.getPermissionTemplates({}));

		this.searchControl = new FormGroup({
			Address: new FormControl('', [Validators.required])
		});

		this.subscribeAll();
		this.onResize();
	}

	subscribeAll(): void {
		this.loadingTemplatesSub = this.loadingTemplate$?.subscribe(
			(permissionsLoading) => {
				this.loadingTemplate = permissionsLoading;
			}
		);
		this.currTemplateSub = this.currentTemplate$.pipe(filter(t => !!t)).subscribe(
			(currentTemplate) => this.loadtemplate(currentTemplate)
		);
		this.updatedSub = this.templateUpdated$.pipe(filter(ed => !!ed)).subscribe(() => {
			this.ref.close();
		});
		this.templateUpdatingSub = this.templateUpdating$.subscribe((updating) => {
			this.updating = updating;
		});
		this.currTemplateLogoSub = this.templateLogo$.subscribe((logo) => {
			this.imgPreview = logo;
		});
		this.siteSub = this.site$.subscribe(currentSite => {
			this.currentSite = currentSite;
			this.customEmailForm = this.fb.group(this.buildForm(currentSite.Name));
		});
	}

	ngOnDestroy(): void {
		this.loadingTemplatesSub?.unsubscribe();
		this.currTemplateSub?.unsubscribe();
		this.updatedSub?.unsubscribe();
		this.templateUpdatingSub?.unsubscribe();
		this.currTemplateLogoSub?.unsubscribe();
		this.siteSub?.unsubscribe();
	}

	async completeMethod(event: { query: any; }): Promise<void> {
		const { AutocompleteService } = await google.maps.importLibrary("places") as google.maps.PlacesLibrary;
		const autocompleteService = new AutocompleteService();

		autocompleteService.getPlacePredictions({ input: event.query }, (predictions, status) => {
			if (status === google.maps.places.PlacesServiceStatus.OK) {
				this.filteredPlace = predictions;
			}
		});
	}

	onSelectSuggestion(event) {
		const placeSelected = event.value.description;
		this.customEmailForm.controls['Address'].setValue(placeSelected);
	}

	loadtemplate(currentTemplate: PermissionTemplate) {
		this.editMode = true;
		this.currentTemplate = currentTemplate;
		this.customEmailForm = this.fb.group(this.buildForm(currentTemplate.Name));
		this.f.Phone.setValue(currentTemplate.Phone);
		this.f.Email.setValue(currentTemplate.Email);
		this.f.Website.setValue(currentTemplate.Website);
		this.f.Address.setValue(currentTemplate.Address);
		this.f.ExtraInfo.setValue(currentTemplate.ExtraInfo);
		if (currentTemplate.ImageId) {
			this.imageId = currentTemplate.ImageId;
			this.store.dispatch(PermissionActions.getPermissionTemplateLogo({ fileId: currentTemplate.ImageId }));
			this.canDeleteUpload = true;
		} else {
			this.canDeleteUpload = false;
		}
	}

	buildForm(siteName: string) {
		const res = {
			Name: [siteName, []],
			Phone: ['', []],
			Email: ['', []],
			Website: ['', []],
			Address: ['', []],
			ExtraInfo: ['', []],
		};
		return res;
	}

	clearField(fieldName: string): void {
		this.customEmailForm.controls[fieldName].setValue('');
	}

	clearLogo(): void {
		this.canDeleteUpload = false;
		this.imageId = '';
		this.fileToUpload = null;
		this.imgPreview = '';
		this.store.dispatch(PermissionActions.getPermissionTemplateLogoSuccess({ fileContent: null }));
	}

	handleFileInput(event) {
		this.checkDimension(event);
		setTimeout(() => {
			this.checkFile(event);
		}, 500);

	}

	checkFile(event) {
		if (event.currentFiles.length > 0) {
			this.fileToUpload = event.currentFiles[0];
			if (!ALLOWED_IMAGE_TYPES.includes(this.fileToUpload.type)) {
				const notification = new Notification("FILE_NOT_COMPATIBLE", this.translate.instant("MAX_UPLOAD_SIZE") + " 900KB", NotificationLevel.Error, '');
				this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true }));

				this.fileToUpload = null;
				this.imgPreview = '';
				this.canDeleteUpload = true;
			} else if (this.fileToUpload.size > MAX_UPLOAD_SIZE) {
				const notification = new Notification("FILE_TOO_BIG", this.translate.instant("MAX_UPLOAD_SIZE") + " 900KB", NotificationLevel.Error, '');
				this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true }));

				this.fileToUpload = null;
				this.imgPreview = '';
				this.canDeleteUpload = true;
			} else if (this.imgHeight > 250 && this.imgWidth > 800) {
				const notification = new Notification("FILE_TOO_BIG", this.translate.instant("CUSTOMIZE_EMAIL_IMAGE_INFO_NEW"), NotificationLevel.Error, '');
				this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true }));

				this.fileToUpload = null;
				this.imgPreview = '';
				this.canDeleteUpload = true;
			} else {
				const myReader: FileReader = new FileReader();
				myReader.onloadend = () => {
					this.image = myReader.result;
					this.store.dispatch(PermissionActions.getPermissionTemplateLogoSuccess({ fileContent: myReader.result.toString() }));
				};
				myReader.readAsDataURL(this.fileToUpload);

				const notification = new Notification("SUCCESS", this.translate.instant("IMAGE_UPLOADED_SUCCESS"), NotificationLevel.Success, '');
				this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true }));

				this.canDeleteUpload = true;
			}
		} else {
			const notification = new Notification("ERROR", "Upload image error", NotificationLevel.Error, '');
			this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true }));
		}
	}


	checkDimension(event): void {
		if (event.currentFiles.length > 0) {
			this.fileToUpload = event.currentFiles[0];
			if (ALLOWED_IMAGE_TYPES.includes(this.fileToUpload.type)) {
				const image: any = event.currentFiles[0];
				const fr = new FileReader();
				fr.onload = () => { // when file has loaded
					const img = new Image();
					img.onload = () => {
						this.imgWidth = img.width;
						this.imgHeight = img.height;
					};

					img.src = fr.result.toString(); // The data URL 
					return false;
				};

				fr.readAsDataURL(image);
			}
		}
	}

	submit() {
		const logo = this.logoUserFile();
		const template = this.importForm();

		if (this.editMode) {
			template.Id = this.currentTemplate.Id;
			this.store.dispatch(PermissionActions.updatePermissionTemplate({ logo, template }));
		} else {
			this.store.dispatch(PermissionActions.savePermissionTemplate({ logo, template }));
		}
	}

	logoUserFile(): UserFile {
		if (!this.fileToUpload) {
			return null;
		}
		return {
			Type: EFileType.PERMISSION_LOGO,
			Name: this.fileToUpload.name,
			Content: btoa(this.imgPreview.toString()),
			MimeType: this.fileToUpload.type,
			SiteId: this.currentSite.Id,
		};
	}

	importForm(): PermissionTemplate {
		const res: PermissionTemplate = {
			Name: this.customEmailForm.value.Name || null,
			Phone: this.customEmailForm.value.Phone || null,
			Email: this.customEmailForm.value.Email || null,
			Website: this.customEmailForm.value.Website || null,
			Address: this.customEmailForm.value.Address || null,
			ExtraInfo: this.customEmailForm.value.ExtraInfo || null,
		};

		if (!this.canDeleteUpload) {
			res.ImageId = null;
		} else if (this.editMode && !this.fileToUpload) {
			res.ImageId = this.imageId;
		}
		return res;
	}

	get f(): any {
		return this.customEmailForm.controls;
	}

	closeDialog(): void {
		this.ref.close();
	}

	// Design
	@HostListener('window:resize')
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	justifyContentStyle(): string {
		return this.onMobileExt() ? "justify-content-center" : "justify-content-start";
	}

	onMobileExt(): boolean {
		return this.screenWidth <= this.MIN_MD_WIDTH_EXT;
	}

	col12Responsive(): string {
		return this.screenWidth <= this.MAX_MD_WIDTH ? 'col-12' : 'col-6';
	}

	// !! USE ONLY ON COL12 RESPONSIVE FIELDS
	dynamicInputTextWidth(): string {
		return this.onMobileExt() ? 'w-full h-2_5rem' : (this.screenWidth <= this.MAX_MD_WIDTH ? "w-30rem h-2rem" : 'w-20rem h-2rem');
	}

	dynamicLongInputTextWidth(): string {
		return (this.onMobileExt()) ? "w-full h-2_5rem" : (this.screenWidth <= this.MAX_MD_WIDTH ? "w-30rem h-2rem" : "w-49rem h-2rem");
	}

	dynamicAutocomplete(): string {
		if (this.onMobileExt()) {
			return "p-autocomplete-panel";
		}
		return "p-autocomplete-panel-right-address";
	}

	dynamicButtonWidth(): string {
		return (this.screenWidth <= this.MIN_TWO_BUTTONS_DIALOG_WIDTH) ? "w-full" : "w-16rem";
	}

	dynamicInputTextFormat(classType: string): string {
		switch (classType) {
			case 'borderRow':
				if (this.onMobileExt()) {
					return "row";
				}
				return "";
			case 'borderInput':
				if (this.onMobileExt()) {
					return "col-12 w-full p-0";
				}
				return "";
			case 'label':
				if (this.onMobileExt()) {
					return "col-12 p-1";
				}
				return "";
			case 'inputText':
				if (this.screenWidth <= this.MAX_MD_WIDTH) {
					if (this.onMobileExt()) {
						return "w-full h-2_5rem"
					}
					return "w-30rem h-2rem";
				}
				return "w-30rem h-2rem";
			default:
				return "";
		}
	}

	showErrors(type: PermissionEmailDialogComponent['f'], classType: string): string {
		let inputTextStyle = '';

		switch (classType) {
			case 'borderRow':
				if (this.onMobileExt()) {
					return 'row';
				}
				return type.errors !== null && type.dirty
					? 'border-bottom-1 border-50'
					: 'border-bottom-1 border-100';
			case 'borderInput':
				if (this.onMobileExt()) {
					inputTextStyle += 'col-12 w-full p-0';
				}
				if (type.errors !== null && type.dirty) {
					inputTextStyle += ' border-bottom-1 border-50';
				} else {
					inputTextStyle += ' border-bottom-1 border-100';
				}
				return inputTextStyle;
			case 'label':
				if (this.onMobileExt()) {
					inputTextStyle += 'col-12 p-1';
				}
				if (type.errors !== null && type.dirty) {
					inputTextStyle += ' text-50';
				}
				return inputTextStyle;
			default:
				return inputTextStyle;
		}
	}

	showErrorsNoValid(classType: string): string {
		switch (classType) {
			case 'borderRow':
				if (this.onMobileExt()) {
					return 'row';
				}
				return 'border-bottom-1 border-100';
			case 'borderInput':
				if (this.onMobileExt()) {
					return 'col-12 w-full p-0 border-bottom-1 border-100';
				}
				return 'border-bottom-1 border-100';
			case 'label':
				if (this.onMobileExt()) {
					return 'col-12 p-1';
				}
				return '';
			default:
				return '';
		}
	}

	showErrorRequiredMessage(type: PermissionEmailDialogComponent['f']): boolean {
		return type.errors !== null && type.dirty;
	}
}
